// Product size finder — progressive enhancement for a real Dawn Size option.
// It deliberately delegates all variant changes to Dawn's existing controls.
if (!window.__productSizeFinderInit) {
  window.__productSizeFinderInit = true;

  const optionName = (control) => (control?.dataset?.optionName || '').trim().toLowerCase();
  const numericSize = (value) => {
    const match = String(value || '').replace(',', '.').match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : null;
  };
  const isPickable = (control) => control && !control.disabled && !control.classList.contains('disabled');
  // The live product configuration may still call this option "Comfort Fit",
  // but the finder presents it exclusively as the Made to Measure route.
  const isMadeToMeasureRouteValue = (value) => /comfort\s*fit|made\s*to\s*measure|a\s*medida/i.test(String(value || ''));

  const attach = (dialog) => {
    if (!dialog) return;
    if (dialog.__productSizeFinderAttached) {
      dialog.__productSizeFinderRefresh?.();
      return;
    }
    dialog.__productSizeFinderAttached = true;

    const host = dialog.closest('product-info') || document;
    const dialogId = dialog.id;
    const triggers = () => [...host.querySelectorAll(`[data-size-finder-open="${dialogId}"]`)];
    const screens = [...dialog.querySelectorAll('[data-size-finder-screen]')];
    const progress = dialog.querySelector('[data-size-finder-progress]');
    const headerTitle = dialog.querySelector('[data-size-finder-header-title]');
    const renderedSizeSource = dialog.querySelector('[data-size-finder-size-source]');
    const fitProfileSource = dialog.querySelector('[data-size-finder-fit-profile]');
    const configSource = dialog.querySelector('[data-size-finder-config]');
    let fitProfile;
    let finderConfig;
    try {
      fitProfile = JSON.parse(fitProfileSource?.textContent || '');
      finderConfig = JSON.parse(configSource?.textContent || '');
    } catch (error) {
      return;
    }
    const QUESTIONS = Array.isArray(finderConfig.questions) ? finderConfig.questions : [];
    const WIDTHS = Array.isArray(finderConfig.widths) ? finderConfig.widths : [];
    const totalSteps = QUESTIONS.length + 1;
    const template = (value, tokens = {}) => String(value || '').replace(/\[([a-z]+)]/gi, (match, key) => (
      Object.prototype.hasOwnProperty.call(tokens, key.toLowerCase()) ? tokens[key.toLowerCase()] : match
    ));
    if (!QUESTIONS.length || !WIDTHS.length || !finderConfig.standardResult || !finderConfig.madeToMeasure) return;
    const state = { questionStep: 0, answers: {}, widthCategory: null, recommendation: null, path: 'measure', usedValue: null };
    const measurementUnit = dialog.dataset.sizeFinderUnit === 'in' ? 'in' : 'cm';
    const measurementUnitLabel = measurementUnit === 'in' ? 'inches' : 'centimeters';
    const toCentimeters = (value) => measurementUnit === 'in' ? value * 2.54 : value;
    const sizeLengthInches = (size) => fitProfile.baseLengthInches
      + (size - fitProfile.baseSize) * fitProfile.fullSizeIncrementInches;
    const halfSizeStepInches = fitProfile.fullSizeIncrementInches / 2;
    const profileLengthMinCm = (sizeLengthInches(fitProfile.minSize) - halfSizeStepInches / 2) * 2.54;
    const profileLengthMaxCm = (sizeLengthInches(fitProfile.maxSize) + halfSizeStepInches / 2) * 2.54;
    const brannockSizeForCentimeters = (centimeters) => fitProfile.baseSize
      + ((centimeters / 2.54) - fitProfile.baseLengthInches) / fitProfile.fullSizeIncrementInches;
    const isProfileSize = (size) => Number.isFinite(size) && size >= fitProfile.minSize && size <= fitProfile.maxSize;
    const standardWidthMmFor = (size) => fitProfile.width.dWidthAtMinSizeMm
      + (size - fitProfile.minSize) * 2 * fitProfile.width.perHalfSizeMm;
    const isStandardWidth = (size, widthMm) => Math.abs(widthMm - standardWidthMmFor(size)) <= fitProfile.width.standardToleranceMm;
    const screenOrder = {
      start: 0,
      'measure-length': 1,
      'measure-width': 2,
      question: 3,
      'question-width': 4,
      'standard-result': 5,
      'made-to-measure-result': 5,
    };
    const screenMotionDuration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 1 : 420;
    let screenTransitionVersion = 0;

    const formBoundControls = (selector) => {
      const formId = dialog.dataset.productFormId;
      const controls = [
        ...host.querySelectorAll(selector),
        ...document.querySelectorAll(selector),
      ];
      return [...new Set(controls)].filter((control) => control.closest('product-info') === host || control.getAttribute('form') === formId);
    };
    const sizeControls = () => formBoundControls('variant-selects input[data-option-name], variant-selects select')
      .filter((control) => {
        if (control.tagName !== 'SELECT') return optionName(control) === 'size' && isPickable(control);
        const isSizeSelect = control.closest('.product-form__input--size-select') || /options\[size\]/i.test(control.name);
        return isSizeSelect && isPickable(control);
      });
    const controlSizes = () => sizeControls().flatMap((control) => {
      if (control.tagName === 'SELECT') {
        return [...control.options]
          .filter((option) => !option.disabled && isProfileSize(numericSize(option.value)))
          .map((option) => ({
            control,
            numeric: numericSize(option.value),
            value: option.value,
            activate: () => {
              control.value = option.value;
              control.dispatchEvent(new Event('change', { bubbles: true }));
              return true;
            },
          }));
      }
      const numeric = numericSize(control.value);
      return isProfileSize(numeric) ? [{ control, numeric, value: control.value, activate: () => { control.click(); return true; } }] : [];
    });
    const renderedSizes = () => [...(renderedSizeSource?.options || [])]
      .filter((option) => isProfileSize(numericSize(option.value)))
      .map((option) => ({
        numeric: numericSize(option.value),
        value: option.value,
        activate: () => {
          const control = sizeControls().find((item) => item.value === option.value);
          if (!control) return false;
          if (control.tagName === 'SELECT') {
            control.value = option.value;
            control.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            control.click();
          }
          return true;
        },
      }));
    const availableSizes = () => {
      const liveSizes = controlSizes();
      return liveSizes.length ? liveSizes : renderedSizes();
    };
    const refreshTriggerAvailability = () => {
      const hasStandardSizes = availableSizes().length > 0;
      triggers().forEach((trigger) => { trigger.hidden = !hasStandardSizes; });
    };
    const madeToMeasureRouteControl = () => {
      const controls = formBoundControls('variant-selects input[data-option-name], variant-selects select');
      for (const control of controls) {
        const isLineControl = control.tagName === 'SELECT'
          ? control.closest('.product-form__input--size-select') === null && /options\[(line|l[ií]nea)\]/i.test(control.name)
          : optionName(control) === 'line';
        if (!isLineControl || !isPickable(control)) continue;

        if (control.tagName === 'SELECT') {
          const option = [...control.options].find((item) => !item.disabled && isMadeToMeasureRouteValue(item.value));
          if (!option) continue;
          return {
            value: option.value,
            activate: () => {
              control.value = option.value;
              control.dispatchEvent(new Event('change', { bubbles: true }));
              return true;
            },
          };
        }

        if (isMadeToMeasureRouteValue(control.value)) {
          return { value: control.value, activate: () => { control.click(); return true; } };
        }
      }
      return null;
    };
    const standardRouteControl = () => {
      const controls = formBoundControls('variant-selects input[data-option-name], variant-selects select');
      for (const control of controls) {
        const isLineControl = control.tagName === 'SELECT'
          ? control.closest('.product-form__input--size-select') === null && /options\[(line|l[ií]nea)\]/i.test(control.name)
          : optionName(control) === 'line';
        if (!isLineControl || !isPickable(control)) continue;

        if (control.tagName === 'SELECT') {
          const option = [...control.options].find((item) => !item.disabled && !isMadeToMeasureRouteValue(item.value));
          if (!option) continue;
          return {
            activate: () => {
              control.value = option.value;
              control.dispatchEvent(new Event('change', { bubbles: true }));
              return true;
            },
          };
        }

        if (!isMadeToMeasureRouteValue(control.value)) {
          return { activate: () => { control.click(); return true; } };
        }
      }
      return null;
    };
    const nearestAvailable = (target) => {
      const choices = controlSizes().length ? controlSizes() : renderedSizes();
      if (!choices.length) return null;
      // Between two sellable sizes, always round UP (a pair that is a little
      // roomy is safer than one that is tight). Explicit rule, not float noise.
      return choices.reduce((best, choice) => {
        const diff = Math.abs(choice.numeric - target);
        const bestDiff = Math.abs(best.numeric - target);
        if (diff < bestDiff - 1e-9) return choice;
        if (Math.abs(diff - bestDiff) <= 1e-9 && choice.numeric > best.numeric) return choice;
        return best;
      });
    };
    const measurement = (key) => {
      const input = dialog.querySelector(`[data-size-finder-measure="${key}"]`);
      const value = Number(input?.value);
      return Number.isFinite(value) && value > 0 ? value : null;
    };
    // A number that cannot be a foot length in the chosen unit is almost always
    // the other unit (27 typed as inches is 27 cm).
    const unitProblem = (value) => {
      if (measurementUnit === 'in' && value > 15) return finderConfig.unitErrorIn;
      if (measurementUnit === 'cm' && value < 40) return finderConfig.unitErrorCm;
      return '';
    };
    const progressFor = (screen) => {
      if (screen === 'start') return 0;
      if (screen === 'measure-length') return 50;
      if (screen === 'measure-width') return 100;
      if (screen === 'question') return ((state.questionStep + 1) / totalSteps) * 100;
      if (screen === 'question-width') return 100;
      return 100;
    };

    const show = (screen) => {
      const next = screens.find((item) => item.dataset.sizeFinderScreen === screen);
      if (!next) return;

      if (headerTitle) {
        const currentStep = screen === 'question-width' ? totalSteps : state.questionStep + 1;
        headerTitle.textContent = screen === 'question' || (screen === 'question-width' && state.path === 'questions')
          ? template(finderConfig.questionHeader, { current: currentStep, total: totalSteps })
          : finderConfig.identity;
      }

      const focusScreen = () => {
        const focusTarget = next.querySelector('[data-size-finder-focus], input, button');
        if (focusTarget) focusTarget.focus();
      };
      const current = screens.find((item) => item.classList.contains('is-active'));

      progress.style.transform = `scaleX(${progressFor(screen) / 100})`;
      if (current === next) {
        requestAnimationFrame(focusScreen);
        return;
      }

      const isForward = (screenOrder[screen] ?? 0) >= (screenOrder[current?.dataset.sizeFinderScreen] ?? 0);
      const enteringClass = isForward ? 'is-entering-forward' : 'is-entering-backward';
      const exitingClass = isForward ? 'is-exiting-forward' : 'is-exiting-backward';
      const transitionVersion = ++screenTransitionVersion;

      screens.forEach((item) => {
        if (item !== current && item !== next) {
          item.classList.remove('is-entering-forward', 'is-entering-backward', 'is-exiting-forward', 'is-exiting-backward');
        }
      });

      next.classList.add('is-active', enteringClass);
      if (current) {
        current.classList.remove('is-active');
        current.classList.remove('is-entering-forward', 'is-entering-backward');
        current.classList.add(exitingClass);
      }

      requestAnimationFrame(() => next.classList.remove(enteringClass));
      window.setTimeout(() => {
        if (transitionVersion !== screenTransitionVersion) return;
        current?.classList.remove('is-exiting-forward', 'is-exiting-backward');
        focusScreen();
      }, screenMotionDuration);
    };

    const reset = () => {
      state.questionStep = 0;
      state.answers = {};
      state.widthCategory = null;
      state.recommendation = null;
      state.path = 'measure';
      dialog.querySelectorAll('[data-size-finder-measure]').forEach((input) => { input.value = ''; });
      const error = dialog.querySelector('[data-size-finder-error]');
      if (error) error.textContent = '';
      show('start');
    };

    const open = () => {
      reset();
      if (!dialog.open) dialog.showModal();
    };
    const close = () => { if (dialog.open) dialog.close(); };
    dialog.__productSizeFinderOpen = open;
    dialog.__productSizeFinderRefresh = refreshTriggerAvailability;

    const showMadeToMeasure = (reason) => {
      const copy = dialog.querySelector('[data-size-finder-made-to-measure-copy]');
      const note = dialog.querySelector('[data-size-finder-made-to-measure-note]');
      const button = dialog.querySelector('[data-size-finder-use-made-to-measure]');
      const message = {
        range: finderConfig.madeToMeasure.range,
        unavailable: finderConfig.madeToMeasure.unavailable,
        width: finderConfig.madeToMeasure.width,
        'width-profile': finderConfig.madeToMeasure.widthProfile,
        inconsistent: finderConfig.madeToMeasure.inconsistent,
        insufficient: finderConfig.madeToMeasure.insufficient,
      };
      copy.textContent = message[reason] || message.range;
      const control = madeToMeasureRouteControl();
      button.hidden = !control;
      if (control) button.textContent = finderConfig.madeToMeasure.action;
      note.textContent = control ? finderConfig.madeToMeasure.availableNote : finderConfig.madeToMeasure.unavailableNote;
      show('made-to-measure-result');
    };

    const showStandard = (entry, source) => {
      if (!entry) return showMadeToMeasure('unavailable');
      state.recommendation = entry;
      const result = dialog.querySelector('[data-size-finder-result-size]');
      const copy = dialog.querySelector('[data-size-finder-result-copy]');
      const note = dialog.querySelector('[data-size-finder-standard-note]');
      result.textContent = entry.value;
      copy.textContent = source === 'measure'
        ? finderConfig.standardResult.measurementWithoutWidth
        : source === 'questions'
          ? finderConfig.standardResult.questions
          : finderConfig.standardResult.brannock;
      if (note) {
        // A somewhat narrow (B) or somewhat wide (E) foot still gets a size,
        // plus an honest pointer to Made to Measure for the exact width.
        const nearStandard = state.widthCategory === 'b' || state.widthCategory === 'e';
        note.textContent = nearStandard && finderConfig.standardResult.widthNote
          ? finderConfig.standardResult.widthNote
          : finderConfig.standardResult.note;
      }
      show('standard-result');
    };

    const resolveMeasure = () => {
      const error = dialog.querySelector('[data-size-finder-error]');
      const length = measurement('length');
      if (!length) {
        error.textContent = template(finderConfig.lengthError, { unit: measurementUnitLabel });
        dialog.querySelector('[data-size-finder-measure="length"]').focus();
        return show('measure-length');
      }
      const lengthInCentimeters = toCentimeters(length);
      if (lengthInCentimeters < profileLengthMinCm || lengthInCentimeters > profileLengthMaxCm) return showMadeToMeasure('range');
      const target = brannockSizeForCentimeters(lengthInCentimeters);
      const entry = nearestAvailable(target);
      if (!entry) return showMadeToMeasure('unavailable');
      // The nearest sellable size must really be near: never hand a US 14 foot a 13.
      if (Math.abs(entry.numeric - target) > 0.75) return showMadeToMeasure('range');
      // Very narrow (A) and very wide (EE) feet fit best made to measure.
      if (state.widthCategory === 'a' || state.widthCategory === 'ee') return showMadeToMeasure('width-profile');
      showStandard(entry, 'measure');
    };

    const createQuestionSlide = (question) => {
      const slide = document.createElement('div');
      const step = document.createElement('p');
      const title = document.createElement('h2');
      const help = document.createElement('p');
      const grid = document.createElement('div');
      const values = availableSizes().map((entry) => entry.numeric).filter((size, index, list) => list.indexOf(size) === index).sort((a, b) => a - b);

      slide.className = 'product-size-finder__question-slide';
      step.className = 'product-size-finder__step';
      step.textContent = template(finderConfig.stepTemplate, { current: state.questionStep + 1, total: totalSteps });
      title.className = 'product-size-finder__title product-size-finder__title--question';
      title.dataset.sizeFinderFocus = '';
      title.tabIndex = -1;
      title.textContent = question.title;
      help.className = 'product-size-finder__lead product-size-finder__lead--question';
      help.textContent = question.help;
      grid.className = 'product-size-finder__answer-grid';

      values.forEach((size) => {
        const button = document.createElement('button');
        button.className = `product-size-finder__answer${state.answers[question.key] === size ? ' is-selected' : ''}`;
        button.type = 'button';
        button.dataset.sizeFinderAnswer = String(size);
        button.textContent = `${size} ${finderConfig.sizeSuffix}`;
        button.addEventListener('click', () => {
          state.answers[question.key] = Number(button.dataset.sizeFinderAnswer);
          button.classList.add('is-selected');
          window.setTimeout(nextQuestion, 120);
        });
        grid.append(button);
      });

      slide.append(step, title, help, grid);
      return slide;
    };

    const renderQuestion = ({ animate = false, direction = 'forward' } = {}) => {
      const question = QUESTIONS[state.questionStep];
      const questionScreen = dialog.querySelector('[data-size-finder-screen="question"]');
      const questionTrack = dialog.querySelector('[data-size-finder-question-track]');
      if (!question || !questionScreen || !questionTrack) return;

      const nextSlide = createQuestionSlide(question);
      const currentSlide = questionTrack.querySelector('.product-size-finder__question-slide');
      const shouldAnimate = animate && questionScreen.classList.contains('is-active') && currentSlide;
      questionScreen.setAttribute('aria-label', `${finderConfig.identity}: ${template(finderConfig.stepTemplate, { current: state.questionStep + 1, total: totalSteps })}. ${question.title}`);

      if (!shouldAnimate) {
        questionTrack.classList.add('is-rebasing');
        questionTrack.classList.remove('is-moving-forward', 'is-at-next', 'is-animating');
        questionTrack.replaceChildren(nextSlide);
        requestAnimationFrame(() => questionTrack.classList.remove('is-rebasing'));
        show('question');
        return;
      }

      questionTrack.classList.add('is-rebasing', 'is-animating');
      questionTrack.classList.remove('is-moving-forward', 'is-at-next');
      if (direction === 'backward') {
        questionTrack.replaceChildren(nextSlide, currentSlide);
        questionTrack.classList.add('is-at-next');
      } else {
        questionTrack.replaceChildren(currentSlide, nextSlide);
      }
      show('question');
      requestAnimationFrame(() => {
        questionTrack.classList.remove('is-rebasing');
        if (direction === 'backward') questionTrack.classList.remove('is-at-next');
        else questionTrack.classList.add('is-moving-forward');
      });
      window.setTimeout(() => {
        questionTrack.classList.add('is-rebasing');
        questionTrack.classList.remove('is-moving-forward', 'is-at-next', 'is-animating');
        questionTrack.replaceChildren(nextSlide);
        requestAnimationFrame(() => questionTrack.classList.remove('is-rebasing'));
      }, screenMotionDuration);
    };

    const renderWidth = () => {
      const grid = dialog.querySelector('[data-size-finder-width-grid]');
      const art = dialog.dataset.sizeFinderWidthArt;
      if (!grid || !art) return;
      grid.replaceChildren();
      const stepLabel = dialog.querySelector('[data-size-finder-screen="question-width"] .product-size-finder__step');
      if (stepLabel) stepLabel.hidden = state.path === 'measure';
      WIDTHS.forEach((width) => {
        const button = document.createElement('button');
        const image = document.createElement('img');
        const artFrame = document.createElement('span');
        const copy = document.createElement('span');
        button.className = `product-size-finder__width-answer${state.widthCategory === width.key ? ' is-selected' : ''}`;
        button.type = 'button';
        button.dataset.sizeFinderWidth = width.key;
        button.style.setProperty('--width-foot-render', width.imageWidth);
        artFrame.className = 'product-size-finder__width-art';
        image.src = art;
        image.alt = '';
        copy.className = 'product-size-finder__width-copy';
        copy.innerHTML = `<strong>${width.label}</strong><small>${width.detail}</small>`;
        if (width.key === 'd') {
          button.classList.add('has-tag');
          const tag = document.createElement('span');
          tag.className = 'product-size-finder__width-tag';
          tag.textContent = finderConfig.widthPopularLabel;
          button.append(tag);
        }
        artFrame.append(image);
        button.append(artFrame, copy);
        button.addEventListener('click', () => {
          state.widthCategory = width.key;
          grid.querySelectorAll('[data-size-finder-width]').forEach((item) => item.classList.toggle('is-selected', item === button));
          window.setTimeout(state.path === 'measure' ? resolveMeasure : resolveQuestions, 120);
        });
        grid.append(button);
      });
      show('question-width');
    };

    const nextQuestion = () => {
      if (state.questionStep < QUESTIONS.length - 1) {
        state.questionStep += 1;
        renderQuestion({ animate: true });
      } else {
        renderWidth();
      }
    };
    const skipQuestion = () => {
      delete state.answers[QUESTIONS[state.questionStep].key];
      nextQuestion();
    };
    const backQuestion = () => {
      if (state.questionStep === 0) show('start');
      else {
        state.questionStep -= 1;
        renderQuestion({ animate: true, direction: 'backward' });
      }
    };
    const resolveQuestions = () => {
      const candidates = QUESTIONS.flatMap((question) => {
        const answer = state.answers[question.key];
        return Number.isFinite(answer) ? [{ size: answer + (question.offset || 0), weight: question.weight }] : [];
      });
      if (!candidates.length) return showMadeToMeasure('insufficient');
      const values = candidates.map((candidate) => candidate.size);
      if (Math.max(...values) - Math.min(...values) > 1) return showMadeToMeasure('inconsistent');
      const totalWeight = candidates.reduce((total, candidate) => total + candidate.weight, 0);
      const target = candidates.reduce((total, candidate) => total + candidate.size * candidate.weight, 0) / totalWeight;
      const entry = nearestAvailable(target);
      if (!entry) return showMadeToMeasure('unavailable');
      if (Math.abs(entry.numeric - target) > 0.75) return showMadeToMeasure('range');
      if (state.widthCategory === 'a' || state.widthCategory === 'ee') return showMadeToMeasure('width-profile');
      showStandard(entry, 'questions');
    };

    const removeMeasurements = () => {
      const formId = dialog.dataset.productFormId;
      const form = formId ? document.getElementById(formId) : null;
      form?.querySelectorAll('[data-size-finder-property]').forEach((node) => node.remove());
    };
    const saveMeasurements = () => {
      const formId = dialog.dataset.productFormId;
      const form = formId ? document.getElementById(formId) : null;
      if (!form) return;
      const value = measurement('length');
      const existing = form.querySelector('[data-size-finder-property="length"]');
      if (!value) { existing?.remove(); return; }
      const property = existing || document.createElement('input');
      property.dataset.sizeFinderProperty = 'length';
      property.type = 'hidden';
      property.name = `properties[${finderConfig.fitPropertyPrefix}: ${finderConfig.lengthLabel} (${measurementUnit})]`;
      property.value = value.toFixed(1);
      if (!existing) form.append(property);
    };
    // If the shopper then picks a different size by hand, the saved foot length
    // no longer matches the pair and must not travel with the order.
    host.addEventListener('change', (event) => {
      const input = event.target;
      if (!state.usedValue || !input || !input.dataset || optionName(input) !== 'size') return;
      if (input.value !== state.usedValue) { removeMeasurements(); state.usedValue = null; }
    });

    const activateSizeAfterStandardRefresh = (value, attempt = 0) => {
      const entry = controlSizes().find((item) => item.value === value);
      if (entry?.activate) {
        entry.activate();
        return;
      }
      if (attempt < 12) window.setTimeout(() => activateSizeAfterStandardRefresh(value, attempt + 1), 100);
    };
    const useSize = () => {
      if (!state.recommendation?.activate) return showMadeToMeasure('unavailable');
      saveMeasurements();
      state.usedValue = state.recommendation.value;
      if (state.recommendation.activate() !== false) {
        close();
        return;
      }

      // The finder can be opened while Made to Measure is the active Line.
      // Its static size list is still useful for recommending, but its inputs
      // are not mounted until Standard Size is active again.
      const standardRoute = standardRouteControl();
      if (!standardRoute?.activate) return showMadeToMeasure('unavailable');
      const value = state.recommendation.value;
      standardRoute.activate();
      close();
      window.setTimeout(() => activateSizeAfterStandardRefresh(value), 100);
    };
    const useMadeToMeasure = () => {
      const control = madeToMeasureRouteControl();
      if (!control) return;
      saveMeasurements();
      control.activate();
      close();
    };

    refreshTriggerAvailability();
    dialog.querySelectorAll('[data-size-finder-close]').forEach((button) => button.addEventListener('click', close));
    dialog.querySelectorAll('[data-size-finder-go]').forEach((button) => button.addEventListener('click', () => {
      const destination = button.dataset.sizeFinderGo;
      if (destination === 'measure-length') state.path = 'measure';
      if (destination === 'question') {
        state.path = 'questions';
        state.questionStep = 0;
        renderQuestion();
      }
      else show(destination);
    }));
    dialog.querySelector('[data-size-finder-next-measure]').addEventListener('click', () => {
      const length = measurement('length');
      const error = dialog.querySelector('[data-size-finder-error]');
      if (!length) { error.textContent = template(finderConfig.lengthError, { unit: measurementUnitLabel }); return; }
      const unitMessage = unitProblem(length);
      if (unitMessage) { error.textContent = template(unitMessage, { unit: measurementUnitLabel }); return; }
      if (toCentimeters(length) < profileLengthMinCm || toCentimeters(length) > profileLengthMaxCm) { showMadeToMeasure('range'); return; }
      error.textContent = '';
      state.path = 'measure';
      state.widthCategory = null;
      renderWidth();
    });
    dialog.querySelector('[data-size-finder-question-back]').addEventListener('click', backQuestion);
    dialog.querySelectorAll('[data-size-finder-question-skip]').forEach((button) => button.addEventListener('click', skipQuestion));
    dialog.querySelector('[data-size-finder-width-back]').addEventListener('click', () => {
      if (state.path === 'measure') { show('measure-length'); return; }
      state.questionStep = QUESTIONS.length - 1;
      renderQuestion({ direction: 'backward' });
    });
    dialog.querySelectorAll('[data-size-finder-width-skip]').forEach((button) => button.addEventListener('click', () => {
      state.widthCategory = null;
      if (state.path === 'measure') resolveMeasure(); else resolveQuestions();
    }));
    dialog.querySelector('[data-size-finder-use-size]').addEventListener('click', useSize);
    dialog.querySelector('[data-size-finder-use-made-to-measure]').addEventListener('click', useMadeToMeasure);
    dialog.querySelectorAll('[data-size-finder-restart]').forEach((button) => button.addEventListener('click', reset));

    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll('button:not([disabled]):not([hidden]), input:not([disabled])')]
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  };

  const boot = () => document.querySelectorAll('dialog[data-product-size-finder]').forEach(attach);
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-size-finder-open]');
    if (!trigger) return;
    const dialog = document.getElementById(trigger.dataset.sizeFinderOpen);
    if (!dialog?.__productSizeFinderOpen) return;
    event.preventDefault();
    dialog.__productSizeFinderOpen();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  new MutationObserver(boot).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', boot);
}
